// Web Speech API hook. Browser-native, free, zero deps. Streams interim
// transcripts via onTranscript so the input field updates live. Falls back
// gracefully when the browser doesn't support speech recognition.

import { useEffect, useRef, useState } from "react";

// Minimal types — TS doesn't ship Web Speech API types in DOM lib reliably.
type SpeechRecognitionResult = { transcript: string; isFinal?: boolean };
type SpeechRecognitionEvent = {
  resultIndex: number;
  results: ArrayLike<ArrayLike<SpeechRecognitionResult>>;
};

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((ev: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((ev: { error: string }) => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognition;

function getSpeechRecognition(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export type VoiceState = "idle" | "listening" | "unsupported" | "denied";

export function useVoiceInput(opts: {
  onTranscript: (text: string, isFinal: boolean) => void;
  onError?: (err: string) => void;
}) {
  const [state, setState] = useState<VoiceState>(() =>
    getSpeechRecognition() ? "idle" : "unsupported",
  );
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  useEffect(() => () => recognitionRef.current?.abort(), []);

  const start = () => {
    const Ctor = getSpeechRecognition();
    if (!Ctor) {
      setState("unsupported");
      return;
    }
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";

    rec.onresult = (ev) => {
      // Concatenate ALL results from index 0 — Web Speech accumulates results
      // across the session, and we want the full transcript so far.
      let finalText = "";
      let interimText = "";
      for (let i = 0; i < ev.results.length; i++) {
        const r = ev.results[i];
        const first = r[0];
        if (!first) continue;
        if (first.isFinal) finalText += first.transcript;
        else interimText += first.transcript;
      }
      const combined = (finalText + interimText).trim();
      opts.onTranscript(combined, finalText.length > 0 && interimText.length === 0);
    };

    rec.onend = () => {
      setState("idle");
      recognitionRef.current = null;
    };

    rec.onerror = (ev) => {
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        setState("denied");
      } else {
        setState("idle");
      }
      opts.onError?.(ev.error);
    };

    try {
      rec.start();
      recognitionRef.current = rec;
      setState("listening");
    } catch {
      setState("idle");
    }
  };

  const stop = () => {
    recognitionRef.current?.stop();
  };

  const toggle = () => {
    if (state === "listening") stop();
    else start();
  };

  return { state, start, stop, toggle };
}
